import { defineWidgetSettings } from "@nexus/widget-sdk";
import { ANIMATE_CHART_CONFIG, MISSING_DATA_CONFIG } from "@nexus/widget-kit";

/** What an author sets on the audience trend; the editor renders it (see `defineWidgetSettings`). */
export const settings = defineWidgetSettings({
  id: "piracy-audience-trend",
  extends: [MISSING_DATA_CONFIG, ANIMATE_CHART_CONFIG],
  properties: {
    audienceDisplay: {
      type: "string",
      title: "Audience",
      description:
        "Safety split shows safe and unsafe categories; Total shows their combined audience.",
      default: "split",
      oneOf: [
        { const: "split", title: "Safety split" },
        { const: "line", title: "Total" },
      ],
      "x-ui": { category: "display", order: 10, summary: true, keepDefault: true },
    },
    showDetails: {
      type: "boolean",
      title: "Summary",
      description: "Summary figures and data freshness available in the chart header",
      default: false,
      "x-ui": { category: "display", group: "show", order: 20, summary: "Summary" },
    },
    summaryPosition: {
      type: "string",
      title: "Summary position",
      description:
        "Side columns support up to three figures. Narrow widgets and larger summaries keep the figures above the chart.",
      default: "top",
      oneOf: [
        { const: "top", title: "Top" },
        { const: "left", title: "Left" },
        { const: "right", title: "Right" },
      ],
      "x-ui": {
        category: "display",
        order: 25,
        visibleIf: { showDetails: true },
      },
    },
    // The summary's figures, one switch each (see `summaryFigures`); report figures are
    // fetched only when switched on.
    summaryShowLatest: {
      type: "boolean",
      title: "Latest audience",
      description: "The audience of the last sample",
      default: true,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 30,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowPeak: {
      type: "boolean",
      title: "Peak",
      description: "The highest audience of the period",
      default: true,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 31,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowAverage: {
      type: "boolean",
      title: "Average viewers",
      description:
        "The report's average audience for the period, as the audience indicator shows it",
      default: false,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 32,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowMax: {
      type: "boolean",
      title: "Max viewers",
      description: "The report's maximum audience for the period",
      default: false,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 33,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowDisrupted: {
      type: "boolean",
      title: "Viewers disrupted",
      description: "The report's estimated viewers disrupted by blocking",
      default: false,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 34,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowSafe: {
      type: "boolean",
      title: "Safe to block",
      description: "The share of the latest audience that can be blocked safely",
      default: true,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 35,
        visibleIf: { showDetails: true },
      },
    },
    summaryShowBlocking: {
      type: "boolean",
      title: "Blocking",
      description: "Whether blocking is running now",
      default: true,
      "x-ui": {
        category: "display",
        group: "summary",
        order: 36,
        visibleIf: { showDetails: true },
      },
    },
    showObserved: {
      type: "boolean",
      title: "Observed traffic",
      description: "Observed CDN traffic for the selected countries, across all platforms",
      default: true,
      "x-ui": { category: "display", group: "show", order: 21, summary: "Observed traffic" },
    },
    showEstimated: {
      type: "boolean",
      title: "Counterfactual traffic",
      description:
        "Model estimate for the selected countries and matching platform group, when available",
      default: true,
      "x-ui": { category: "display", group: "show", order: 22, summary: "Counterfactual" },
    },
    showBlocking: {
      type: "boolean",
      title: "Blocking periods",
      description:
        "Bands for the periods during which blocking ran; grey where it does not reach the selected countries",
      default: true,
      "x-ui": { category: "display", group: "show", order: 23, summary: "Blocking periods" },
    },
    // Which Blocking periods widget drives this chart: a link the host keeps between widgets.
    spotlight: {
      type: "string",
      title: "Blocking spotlight",
      description: "A Blocking periods widget that highlights its period on this chart",
      "x-ui": {
        category: "display",
        order: 75,
        host: "blockingPeriodSelection.driver",
        emptyLabel: "Off",
      },
    },
    spotlightEffect: {
      type: "string",
      title: "Spotlight effect",
      description:
        "Focus dims the rest of the chart and marks the period's peak; Band only emphasizes its band.",
      default: "focus",
      oneOf: [
        { const: "focus", title: "Focus" },
        { const: "band", title: "Band only" },
      ],
      "x-ui": {
        category: "display",
        order: 76,
        visibleIf: { "$blockingPeriodSelection.driver": { set: true } },
      },
    },
    animateChart: {
      description:
        "The audience sweeps in, then the blocking periods and counterfactual appear. Off: drawn at once",
    },
  },
});
